import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { useRouter } from '@core/utils/utils'
import useJwt from '@/auth/jwt/useJwt'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import useEventsUpload from "@/views/organization/confirmation-planning/events-upload/useEventsUpload"
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommon from "@/views/organization/useCommon"

export default function useWorkerAdd() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router } = useRouter()
  const { bulkEventsUpload } = useEventsUpload()
  const { createItem, createCognitoUser } = useLambda()
  const { assignSupervisorsBasedOnRoles, assignSupervisorsBasedOnDepartment } = useCommon()
  const userData = store.state?.userStore?.userData
  const { massive_pc_upload_default } = JSON.parse(localStorage.getItem('clientData') || '{}')
  const worker = ref({ privileges: "employee", isPCUploadEnabled: massive_pc_upload_default || false })
  const isSubmitting = ref(false)
  const collection = 'worker'

  const create = async data => {
    isSubmitting.value = true

    const payload = {
      client_id: oid(userData.client.$oid)
    }

    if (data.name?.value) payload.name = data.name.value
    if (data.email?.value) payload.email = data.email.value
    if (data.supervisors?.value) payload.supervisors = data.supervisors.value.map(e => oid(e)) || []
    if (data.locations?.value) payload.locations = data.locations.value.map(e => oid(e)) || []
    if (data.physical_locations?.value) payload.physical_locations = data.physical_locations.value.map(e => oid(e)) || []
    if (data.roles?.value) payload.roles = data.roles.value.map(e => oid(e)) || []
    if (data.privileges?.value) payload.privileges = data.privileges?.value

    const role = data.privileges?.value || ''

    try {
      const { insertedId } = await createItem({ collection, payload })

      // Update supervisors based on the selected roles
      const workerData = {
        _id: insertedId,
        locations: data.locations?.value || [],
      }
      assignSupervisorsBasedOnRoles(data.roles?.value || [], [], workerData)

      // Update supervisors based on the selected departments
      assignSupervisorsBasedOnDepartment(data.locations?.value || [], [], insertedId)

      if (role === 'consultant' || role === 'director' || role === 'supervisor') {
        try {
          const cognitoResult = await createCognitoUser({ email: data.email.value, name: data.name.value })

          if (cognitoResult.error) {
            throw new Error(`Cognito user creation failed: ${cognitoResult}`)
          }
          console.log("user created")
        } catch (error) {
          console.log(`Error creating user ${data.email.value}`)
          console.error(error)
          showErrorMessage(i18n.t('message.cognito_user_error'))
        }
      }

      showSuccessMessage(i18n.t('message.worker_added'))

      // Bulk P.C. (events) Upload
      if (data.isPCUploadEnabled?.value) await bulkEventsUpload([insertedId], false)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.worker_error'))
    } finally {
      router.push({ name: 'organization-worker-list' })
      isSubmitting.value = false
    }
  }

  return {
    create,
    worker,
    isSubmitting,
  }
}
